import type { CmmdProviderUsageSnapshot } from "@cmmd-forge/contracts";

import type { ProviderUsageAccountView } from "~/usage/useProviderUsage";
import { resolveEffectiveAccountId } from "./rateLimitBannerSelection";

export type ComposerCreditGateDecision =
  | { readonly kind: "ok" }
  | { readonly kind: "warn"; readonly message: string }
  | { readonly kind: "block"; readonly message: string };

/** Mirrors CMMD's active-poll staleness window; passive telemetry is rarer. */
const SNAPSHOT_FRESH_MS = 20 * 60 * 1000;

function isWindowFull(
  window: {
    readonly usedPercent: number;
    readonly status: string;
  } | null,
): boolean | null {
  if (window === null) return null;
  return window.usedPercent >= 100 || window.status === "rejected";
}

function isSnapshotFresh(snapshot: CmmdProviderUsageSnapshot, nowMs: number): boolean {
  if (snapshot.stale) return false;
  const observedMs = new Date(snapshot.observedAt).getTime();
  if (Number.isNaN(observedMs)) return false;
  return nowMs - observedMs <= SNAPSHOT_FRESH_MS;
}

function formatCredits(credits: number): string {
  return credits.toLocaleString("en-US");
}

/**
 * Pre-send gate for Codex rate-limit headroom (FOR-32770).
 *
 * Only ever restricts a send when the evidence is strong: both reported
 * windows full, credits known to be zero, and the observation fresh. Every
 * unknown - no snapshot, a missing window, an unreadable balance, stale data -
 * degrades the decision to at most a warning, because a false block stops real
 * work while a missed warning only skips a heads-up. Claude sends are never
 * gated: the credits concept is Codex-specific.
 */
export function resolveCodexCreditGate(input: {
  /** ProviderKind from ModelSelection; only codex is ever gated. */
  readonly provider: string;
  readonly providerAccountId: string | undefined;
  readonly accounts: ReadonlyArray<ProviderUsageAccountView>;
  readonly nowMs: number;
}): ComposerCreditGateDecision {
  if (input.provider !== "codex") return { kind: "ok" };

  const effectiveAccountId = resolveEffectiveAccountId({
    provider: "codex",
    currentProviderAccountId: input.providerAccountId,
    accounts: input.accounts.map((entry) => entry.account),
  });
  const snapshot = input.accounts.find(
    (entry) =>
      entry.account.provider === "codex" && entry.account.providerAccountId === effectiveAccountId,
  )?.snapshot;
  if (!snapshot) return { kind: "ok" };

  const fiveHourFull = isWindowFull(snapshot.fiveHour);
  const sevenDayFull = isWindowFull(snapshot.sevenDay);
  if (fiveHourFull !== true || sevenDayFull !== true) return { kind: "ok" };

  const fresh = isSnapshotFresh(snapshot, input.nowMs);
  if (snapshot.credits === null) {
    return {
      kind: "warn",
      message:
        "Both Codex rate windows are full and the credits balance was not reported. " +
        "Sending may draw down Codex credits.",
    };
  }
  if (snapshot.credits > 0) {
    return {
      kind: "warn",
      message: `Both Codex rate windows are full. Sending will draw down Codex credits: ${formatCredits(snapshot.credits)} left.`,
    };
  }
  if (!fresh) {
    return {
      kind: "warn",
      message:
        "Both Codex rate windows were full with 0 Codex credits left, but this reading may be out of date.",
    };
  }
  return {
    kind: "block",
    message:
      "Both Codex rate windows are full and no Codex credits remain on this account. " +
      "Wait for a window to reset or add Codex credits.",
  };
}
